/**
 * Analytics Utility for API Explorer
 *
 * Hypothesis-based GA4 event tracking using react-ga4.
 * All events are prefixed with "api_explorer_" (or "api_explorer_predev_" in predev).
 * Uses the same GA4 property as the docs site for cross-product funnel analysis.
 *
 * Every event maps to a hypothesis in the analytics spec — each answers a specific
 * business or product question rather than tracking for tracking's sake.
 */

import ReactGA from 'react-ga4';
import { config } from '@/config';

let initialized = false;

/**
 * Initialize GA4 tracking. Call once on app load.
 */
export function initGA4(measurementId: string): void {
  if (initialized) return;
  try {
    ReactGA.initialize(measurementId);
    initialized = true;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[Analytics] GA4 initialization failed:', error);
    }
  }
}

/**
 * Get the event name prefix based on environment.
 * Predev uses a separate prefix to avoid polluting production analytics.
 */
function getPrefix(): string {
  return config.appEnv === 'predev' ? 'api_explorer_predev_' : 'api_explorer_';
}

/**
 * Track a custom GA4 event with parameters.
 * Automatically prepends the environment-aware prefix.
 */
function trackEvent(
  eventSuffix: string,
  parameters?: Record<string, unknown>
): void {
  const eventName = `${getPrefix()}${eventSuffix}`;
  try {
    if (parameters) {
      ReactGA.event(eventName, parameters);
    } else {
      ReactGA.event(eventName);
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics]', eventName, parameters);
    }
  } catch (error) {
    // Analytics should never break the app
    if (process.env.NODE_ENV === 'development') {
      console.error('[Analytics] Event tracking failed:', error);
    }
  }
}

// ---------------------------------------------------------------------------
// Hypothesis-based GA4 events (31 events across 13 categories)
// ---------------------------------------------------------------------------

export const explorerAnalytics = {
  // ── 1. API Execution & Usage Patterns ────────────────────────────────

  /** H1.1 — Run button usage by auth state */
  apiExecuted: (params: {
    resource: string;
    endpoint: string;
    httpMethod: string;
    isLoggedIn: boolean;
    sdkLanguage: string;
  }) => {
    trackEvent('api_executed', {
      resource: params.resource,
      endpoint: params.endpoint,
      http_method: params.httpMethod,
      is_logged_in: params.isLoggedIn,
      sdk_language: params.sdkLanguage,
    });
  },

  /** H1.2 — API error rates by endpoint */
  apiError: (params: {
    resource: string;
    endpoint: string;
    httpMethod: string;
    errorType: string;
    errorMessage: string;
    httpStatus?: number;
  }) => {
    trackEvent('api_error', {
      resource: params.resource,
      endpoint: params.endpoint,
      http_method: params.httpMethod,
      error_type: params.errorType,
      error_message: params.errorMessage.slice(0, 200),
      http_status: params.httpStatus,
    });
  },

  // ── 2. Code Sample Engagement ────────────────────────────────────────

  /** H2.1 — Request code copy behavior */
  playgroundRequestCopied: (params: {
    resource: string;
    endpoint: string;
    sdkLanguage: string;
    codeLength: number;
    isLoggedIn: boolean;
  }) => {
    trackEvent('playground_request_copied', {
      resource: params.resource,
      endpoint: params.endpoint,
      sdk_language: params.sdkLanguage,
      code_length: params.codeLength,
      is_logged_in: params.isLoggedIn,
    });
  },

  /** H2.2 — Text selection intent in code area */
  playgroundRequestSelected: (params: {
    resource: string;
    endpoint: string;
    sdkLanguage: string;
    selectionLength: number;
  }) => {
    trackEvent('playground_request_selected', {
      resource: params.resource,
      endpoint: params.endpoint,
      sdk_language: params.sdkLanguage,
      selection_length: params.selectionLength,
    });
  },

  /** H2.3 — Pre-built code sample adoption (select) */
  codeSampleSelected: (params: {
    resource: string;
    endpoint: string;
    sampleDescription: string;
    sampleIndex: number;
  }) => {
    trackEvent('code_sample_selected', {
      resource: params.resource,
      endpoint: params.endpoint,
      sample_description: params.sampleDescription,
      sample_index: params.sampleIndex,
    });
  },

  /** H2.3 — Pre-built code sample adoption (clear) */
  codeSampleCleared: (params: { resource: string; endpoint: string }) => {
    trackEvent('code_sample_cleared', {
      resource: params.resource,
      endpoint: params.endpoint,
    });
  },

  /** H2.4 — Response copy behavior */
  responseCopied: (params: {
    resource: string;
    endpoint: string;
    responseLength: number;
    isErrorResponse: boolean;
  }) => {
    trackEvent('response_copied', {
      resource: params.resource,
      endpoint: params.endpoint,
      response_length: params.responseLength,
      is_error_response: params.isErrorResponse,
    });
  },

  // ── 3. Search Behavior ───────────────────────────────────────────────

  /** H3.1 — Search usage and conversion */
  searchPerformed: (params: {
    searchQuery: string;
    resultsCount: number;
    resultClicked: boolean;
    resultPosition?: number;
    resultResource?: string;
    resultEndpoint?: string;
  }) => {
    trackEvent('search_performed', {
      search_query: params.searchQuery.slice(0, 100),
      results_count: params.resultsCount,
      result_clicked: params.resultClicked,
      result_position: params.resultPosition,
      result_resource: params.resultResource,
      result_endpoint: params.resultEndpoint,
    });
  },

  // ── 4. SDK / Language Preferences ────────────────────────────────────

  /** H4.1 — SDK language switching patterns */
  sdkChanged: (params: {
    fromLanguage: string;
    toLanguage: string;
    triggerSource: string;
    resource?: string;
    endpoint?: string;
    isLoggedIn: boolean;
  }) => {
    trackEvent('sdk_changed', {
      from_language: params.fromLanguage,
      to_language: params.toLanguage,
      trigger_source: params.triggerSource,
      resource: params.resource,
      endpoint: params.endpoint,
      is_logged_in: params.isLoggedIn,
    });
  },

  // ── 5. Authentication & Login Funnel ─────────────────────────────────

  /** H5.1 — Site switching behavior */
  siteChanged: (params: {
    fromSite: string;
    toSite: string;
    isLoggedIn: boolean;
  }) => {
    trackEvent('site_changed', {
      from_site: params.fromSite,
      to_site: params.toSite,
      is_logged_in: params.isLoggedIn,
    });
  },

  /** H5.2 — Login intent */
  loginClicked: (params: {
    source: string;
    currentResource?: string;
    currentEndpoint?: string;
  }) => {
    trackEvent('login_clicked', {
      source: params.source,
      current_resource: params.currentResource,
      current_endpoint: params.currentEndpoint,
    });
  },

  /** H5.2 — Logout intent */
  logoutClicked: (params: { siteName: string }) => {
    trackEvent('logout_clicked', {
      site_name: params.siteName,
    });
  },

  /** H5.3 — Login success */
  loginCompleted: (params: { siteName: string; loginMethod: string }) => {
    trackEvent('login_completed', {
      site_name: params.siteName,
      login_method: params.loginMethod,
    });
  },

  /** H5.3 — Auto-login detection */
  autoLoginTriggered: (params: { siteName: string; loginMethod: string }) => {
    trackEvent('auto_login_triggered', {
      site_name: params.siteName,
      login_method: params.loginMethod,
    });
  },

  // ── 6. Page & Endpoint Discovery ─────────────────────────────────────

  /** H6.1 — Playground page popularity */
  playgroundPageViewed: (params: {
    resource: string;
    endpointName: string;
    httpMethod: string;
    hasCodeSamples: boolean;
    isLoggedIn: boolean;
  }) => {
    trackEvent('playground_page_viewed', {
      resource: params.resource,
      endpoint_name: params.endpointName,
      http_method: params.httpMethod,
      has_code_samples: params.hasCodeSamples,
      is_logged_in: params.isLoggedIn,
    });
  },

  /** H6.2 — Homepage to playground navigation path */
  endpointNavigated: (params: {
    resource: string;
    endpoint: string;
    httpMethod: string;
    navigationSource: string;
    isLoggedIn: boolean;
  }) => {
    trackEvent('endpoint_navigated', {
      resource: params.resource,
      endpoint: params.endpoint,
      http_method: params.httpMethod,
      navigation_source: params.navigationSource,
      is_logged_in: params.isLoggedIn,
    });
  },

  // ── 7. Navigation ───────────────────────────────────────────────────

  /** H6.3 — Resource sidebar engagement */
  resourceSelected: (params: {
    resource: string;
    previousResource?: string;
    isLoggedIn: boolean;
  }) => {
    trackEvent('resource_selected', {
      resource: params.resource,
      previous_resource: params.previousResource,
      is_logged_in: params.isLoggedIn,
    });
  },

  /** H11.1 — Homepage engagement */
  homepageViewed: (params: {
    isLoggedIn: boolean;
    resourcesCount: number;
    referrer?: string;
  }) => {
    trackEvent('homepage_viewed', {
      is_logged_in: params.isLoggedIn,
      resources_count: params.resourcesCount,
      referrer: params.referrer,
    });
  },

  // ── 8. Export & Sharing ──────────────────────────────────────────────

  /** H7.1 — Postman export adoption */
  postmanExported: (params: {
    resource: string;
    endpoint: string;
    hasDescription: boolean;
    isLoggedIn: boolean;
  }) => {
    trackEvent('postman_exported', {
      resource: params.resource,
      endpoint: params.endpoint,
      has_description: params.hasDescription,
      is_logged_in: params.isLoggedIn,
    });
  },

  /** H7.2 — Form sharing usage */
  formShared: (params: {
    resource: string;
    endpoint: string;
    isLoggedIn: boolean;
  }) => {
    trackEvent('form_shared', {
      resource: params.resource,
      endpoint: params.endpoint,
      is_logged_in: params.isLoggedIn,
    });
  },

  // ── 9. Form Interaction Depth ────────────────────────────────────────

  /** H8.1 — Empty fields toggle usage */
  emptyFieldsToggled: (params: {
    isVisible: boolean;
    resource: string;
    endpoint: string;
    isLoggedIn: boolean;
  }) => {
    trackEvent('empty_fields_toggled', {
      is_visible: params.isVisible,
      resource: params.resource,
      endpoint: params.endpoint,
      is_logged_in: params.isLoggedIn,
    });
  },

  /** H8.2 — Form clear/reset behavior */
  formCleared: (params: {
    resource: string;
    endpoint: string;
    fieldsFilledCount: number;
    isLoggedIn: boolean;
  }) => {
    trackEvent('form_cleared', {
      resource: params.resource,
      endpoint: params.endpoint,
      fields_filled_count: params.fieldsFilledCount,
      is_logged_in: params.isLoggedIn,
    });
  },

  // ── 10. Feedback & Satisfaction ──────────────────────────────────────

  /** H9.1 — Feedback funnel: opened */
  feedbackOpened: (params: { context: string; isLoggedIn: boolean }) => {
    trackEvent('feedback_opened', {
      context: params.context,
      is_logged_in: params.isLoggedIn,
    });
  },

  /** H9.1 — Feedback funnel: rating selected */
  feedbackRatingSelected: (params: { rating: string; context: string }) => {
    trackEvent('feedback_rating_selected', {
      rating: params.rating,
      context: params.context,
    });
  },

  /** H9.1 — Feedback funnel: submitted */
  feedbackSubmitted: (params: {
    rating: string;
    hasComment: boolean;
    commentLength: number;
    context: string;
    isLoggedIn: boolean;
  }) => {
    trackEvent('feedback_submitted', {
      rating: params.rating,
      has_comment: params.hasComment,
      comment_length: params.commentLength,
      context: params.context,
      is_logged_in: params.isLoggedIn,
    });
  },

  // ── 11. AI Code Generator Adoption ───────────────────────────────────

  /** H10.2 — AI suggestion click-through */
  aiSuggestionClicked: (params: {
    suggestionText: string;
    suggestionPosition: number;
    sdkLanguage: string;
    isLoggedIn: boolean;
  }) => {
    trackEvent('ai_suggestion_clicked', {
      suggestion_text: params.suggestionText.slice(0, 100),
      suggestion_position: params.suggestionPosition,
      sdk_language: params.sdkLanguage,
      is_logged_in: params.isLoggedIn,
    });
  },

  /** H10.3 — AI-recommended endpoint navigation */
  aiRecommendationClicked: (params: {
    resource: string;
    endpoint: string;
    httpMethod: string;
    recommendationSource: string;
    isLoggedIn: boolean;
  }) => {
    trackEvent('ai_recommendation_clicked', {
      resource: params.resource,
      endpoint: params.endpoint,
      http_method: params.httpMethod,
      recommendation_source: params.recommendationSource,
      is_logged_in: params.isLoggedIn,
    });
  },

  /** H10.4 — AI code block copy */
  aiCodeCopied: (params: {
    language: string;
    codeLength: number;
    isLoggedIn: boolean;
  }) => {
    trackEvent('ai_code_copied', {
      language: params.language,
      code_length: params.codeLength,
      is_logged_in: params.isLoggedIn,
    });
  },

  // ── 12. External Link & Documentation Exits ──────────────────────────

  /** H12.1 — API documentation link click-through */
  docsLinkClicked: (params: {
    resource: string;
    endpoint: string;
    linkType: string;
  }) => {
    trackEvent('docs_link_clicked', {
      resource: params.resource,
      endpoint: params.endpoint,
      link_type: params.linkType,
    });
  },

  /** H12.2 — Chargebee site link usage */
  chargebeeSiteLinkClicked: (params: {
    siteName: string;
    resource: string;
    endpoint: string;
  }) => {
    trackEvent('chargebee_site_link_clicked', {
      site_name: params.siteName,
      resource: params.resource,
      endpoint: params.endpoint,
    });
  },

  // ── 13. Unsaved Changes & Data Loss Prevention ───────────────────────

  /** H13.1 — Unsaved changes dialog impact */
  unsavedChangesAction: (params: {
    action: string;
    variant: string;
    triggerSource?: string;
    triggerAction?: string;
  }) => {
    trackEvent('unsaved_changes_action', {
      action: params.action,
      variant: params.variant,
      trigger_source: params.triggerSource,
      trigger_action: params.triggerAction,
    });
  },

  // ── Event Explorer Events (retained from previous implementation) ────

  /** Track when user views the event explorer page */
  eventPageViewed: () => {
    trackEvent('event_page_viewed');
  },

  /** Track when user selects an event from the list */
  eventSelected: (params: { eventType: string; source?: string }) => {
    trackEvent('event_selected', {
      event_type: params.eventType,
      source: params.source,
    });
  },

  /** Track when user applies filters on the events list */
  eventFilterApplied: (params: {
    filterType:
      | 'source'
      | 'eventType'
      | 'timeRange'
      | 'webhookStatus'
      | 'search';
    filterValue: string;
  }) => {
    trackEvent('event_filter_applied', {
      filter_type: params.filterType,
      filter_value: params.filterValue,
    });
  },

  /** Track when user forwards an event to a server */
  eventForwarded: (params: {
    eventType: string;
    forwardMode: 'proxy' | 'direct';
    isSuccess: boolean;
    statusCode?: number;
    durationMs?: number;
  }) => {
    trackEvent('event_forwarded', {
      event_type: params.eventType,
      forward_mode: params.forwardMode,
      is_success: params.isSuccess,
      status_code: params.statusCode,
      duration_ms: params.durationMs,
    });
  },

  /** Track when user configures event forwarding settings */
  eventForwardConfigured: (params: { forwardMode: 'proxy' | 'direct' }) => {
    trackEvent('event_forward_configured', {
      forward_mode: params.forwardMode,
    });
  },

  /** Track when user switches between proxy and direct forwarding modes in config modal */
  eventForwardModeChanged: (params: {
    fromMode: 'proxy' | 'direct';
    toMode: 'proxy' | 'direct';
  }) => {
    trackEvent('event_forward_mode_changed', {
      from_mode: params.fromMode,
      to_mode: params.toMode,
    });
  },

  /** Track when user switches detail tabs (JSON / Webhooks / Forward) */
  eventDetailTabSwitched: (params: { tabId: string; eventType?: string }) => {
    trackEvent('event_detail_tab_switched', {
      tab_id: params.tabId,
      event_type: params.eventType,
    });
  },

  /** Track when user copies the event JSON payload */
  eventPayloadCopied: (params: { payloadSize: number; eventType?: string }) => {
    trackEvent('event_payload_copied', {
      payload_size: params.payloadSize,
      event_type: params.eventType,
    });
  },

  /** Track when user copies an event ID */
  eventIdCopied: (params: { eventType?: string }) => {
    trackEvent('event_id_copied', {
      event_type: params.eventType,
    });
  },

  /** Track when user copies the forward response */
  eventForwardResponseCopied: (params: { responseSize: number }) => {
    trackEvent('event_forward_response_copied', {
      response_size: params.responseSize,
    });
  },

  /** Track when user clicks an external link to Chargebee from events */
  eventExternalLinkClicked: (params: {
    source: string;
    eventType?: string;
  }) => {
    trackEvent('event_external_link_clicked', {
      source: params.source,
      event_type: params.eventType,
    });
  },

  /** Track when user downloads a proxy sample */
  eventForwardSampleDownloaded: (params: {
    language: string;
    filename: string;
  }) => {
    trackEvent('event_forward_sample_downloaded', {
      language: params.language,
      filename: params.filename,
    });
  },

  /** Track when user toggles auto-forward */
  eventAutoForwardToggled: (params: {
    enabled: boolean;
    forwardMode?: string;
  }) => {
    trackEvent('event_auto_forward_toggled', {
      enabled: params.enabled,
      forward_mode: params.forwardMode,
    });
  },

  /** Track when user merges live buffer (new events loaded) */
  eventLiveBufferMerged: (params: { eventCount: number }) => {
    trackEvent('event_live_buffer_merged', {
      event_count: params.eventCount,
    });
  },

  /** Track when user searches events by ID */
  eventSearchPerformed: (params: { searchQuery: string }) => {
    trackEvent('event_search_performed', {
      search_query: params.searchQuery.slice(0, 100),
    });
  },

  /** Track when user manually refreshes the event list */
  eventListRefreshed: (params: { isManual: boolean }) => {
    trackEvent('event_list_refreshed', {
      is_manual: params.isManual,
    });
  },
};
